import { useState, useEffect, useCallback } from 'react';
import type { GameMode, IntensityLevel, GenderType, GameSettings } from '../types';

const STORAGE_KEY = 'intimateGamesSettings';

export function useGameState() {
  const [gameMode, setGameMode] = useState<GameMode>('start');
  const [selectedOption, setSelectedOption] = useState<'truth' | 'dare' | null>(null);
  const [currentContent, setCurrentContent] = useState('');
  const [intensity, setIntensity] = useState<IntensityLevel>('moderate');
  const [menuOpen, setMenuOpen] = useState(false);
  const [playerNames, setPlayerNames] = useState<string[]>(['Player 1', 'Player 2']);
  const [playerGenders, setPlayerGenders] = useState<GenderType[]>(['male', 'female']);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [animation, setAnimation] = useState('');
  const [disableTruth, setDisableTruth] = useState(false);
  const [disableDare, setDisableDare] = useState(false);
  const [randomMode, setRandomMode] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        const settings: GameSettings = JSON.parse(savedSettings);
        if (settings.playerNames?.length > 0) {
          setPlayerNames(settings.playerNames);
        }
        if (settings.playerGenders?.length > 0) {
          setPlayerGenders(settings.playerGenders);
        }
        if (settings.intensity) {
          setIntensity(settings.intensity);
        }
        if (typeof settings.disableTruth === 'boolean') {
          setDisableTruth(settings.disableTruth);
        }
        if (typeof settings.disableDare === 'boolean') {
          setDisableDare(settings.disableDare);
        }
        if (typeof settings.randomMode === 'boolean') {
          setRandomMode(settings.randomMode);
        }
      }
    } catch (error) {
      console.error('Error loading settings from localStorage:', error);
    }
    setIsLoaded(true);
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    if (!isLoaded || typeof window === 'undefined') return;

    const settings: GameSettings = {
      playerNames,
      playerGenders,
      intensity,
      disableTruth,
      disableDare,
      randomMode
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings to localStorage:', error);
    }
  }, [playerNames, playerGenders, intensity, disableTruth, disableDare, randomMode, isLoaded]);

  const processContent = useCallback((content: string): string => {
    if (!content.includes('{0}') && !content.includes('{1}')) {
      return content;
    }

    // Replace {0} with current player's name
    let processedContent = content.replace(/\{0\}/g, playerNames[currentPlayer]);

    // Find an opposite gender player for {1}
    const currentGender = playerGenders[currentPlayer];
    const oppositeGender = currentGender === 'male' ? 'female' : 'male';

    // Find players with opposite gender
    const oppositeGenderPlayers = playerNames.filter((_, index) =>
      playerGenders[index] === oppositeGender
    );

    // If there are opposite gender players, randomly select one
    if (oppositeGenderPlayers.length > 0) {
      const randomIndex = Math.floor(Math.random() * oppositeGenderPlayers.length);
      const randomOppositePlayer = oppositeGenderPlayers[randomIndex];
      processedContent = processedContent.replace(/\{1\}/g, randomOppositePlayer);
    } else {
      // If no opposite gender players, use another random player
      const otherPlayers = playerNames.filter((_, index) => index !== currentPlayer);
      if (otherPlayers.length > 0) {
        const randomIndex = Math.floor(Math.random() * otherPlayers.length);
        processedContent = processedContent.replace(/\{1\}/g, otherPlayers[randomIndex]);
      } else {
        // Fallback if there's only one player
        processedContent = processedContent.replace(/\{1\}/g, 'your partner');
      }
    }

    return processedContent;
  }, [playerNames, playerGenders, currentPlayer]);

  const handleOptionSelect = useCallback((option: 'truth' | 'dare', rawContent: string) => {
    setSelectedOption(option);
    setAnimation('fade-out');

    setTimeout(() => {
      setCurrentContent(processContent(rawContent));
      setGameMode('display');
      setAnimation('fade-in');
    }, 300);
  }, [processContent]);

  const nextTurn = useCallback(() => {
    setAnimation('slide-out');

    setTimeout(() => {
      setCurrentPlayer((prev) => (prev + 1) % playerNames.length);

      // If one option is disabled, automatically stay in display mode
      if ((disableTruth && !disableDare) || (!disableTruth && disableDare)) {
        // Stay in select mode but will auto-select
      }

      setGameMode('select');
      setSelectedOption(null);
      setAnimation('slide-in');
    }, 300);
  }, [playerNames.length, disableTruth, disableDare]);

  const resetGame = useCallback(() => {
    setGameMode('start');
    setSelectedOption(null);
    setCurrentContent('');
    setCurrentPlayer(0);
    setMenuOpen(false);
    setAnimation('');
  }, []);

  const addPlayer = useCallback(() => {
    setPlayerNames(prev => [...prev, `Player ${prev.length + 1}`]);
    setPlayerGenders(prev => [...prev, 'male']);
  }, []);

  const updatePlayerName = useCallback((index: number, newName: string) => {
    setPlayerNames(prev => prev.map((name, i) => i === index ? newName : name));
  }, []);

  const toggleGender = useCallback((index: number) => {
    setPlayerGenders(prev => prev.map((gender, i) =>
      i === index ? (gender === 'male' ? 'female' : 'male') : gender
    ));
  }, []);

  const removePlayer = useCallback((index: number) => {
    if (playerNames.length <= 2) return; // Keep minimum 2 players
    setPlayerNames(prev => prev.filter((_, i) => i !== index));
    setPlayerGenders(prev => prev.filter((_, i) => i !== index));
    // Adjust current player if needed
    if (currentPlayer >= index && currentPlayer > 0) {
      setCurrentPlayer(prev => prev - 1);
    }
  }, [playerNames.length, currentPlayer]);

  const startGame = useCallback(() => {
    setGameMode('select');
    setAnimation('fade-in');
  }, []);

  const skipQuestion = useCallback((rawContent: string) => {
    setAnimation('fade-out');
    setTimeout(() => {
      setCurrentContent(processContent(rawContent));
      setAnimation('fade-in');
    }, 300);
  }, [processContent]);

  // Auto-select if only one option is enabled
  useEffect(() => {
    if (gameMode === 'select') {
      if (disableTruth && !disableDare && !selectedOption) {
        // Will be handled by the component that calls getRandomQuestion
      } else if (!disableTruth && disableDare && !selectedOption) {
        // Will be handled by the component that calls getRandomQuestion
      }
    }
  }, [gameMode, disableTruth, disableDare, selectedOption]);

  return {
    // State
    gameMode,
    selectedOption,
    currentContent,
    intensity,
    menuOpen,
    playerNames,
    playerGenders,
    currentPlayer,
    animation,
    disableTruth,
    disableDare,
    randomMode,
    isLoaded,

    // Setters
    setGameMode,
    setIntensity,
    setMenuOpen,
    setDisableTruth,
    setDisableDare,
    setRandomMode,

    // Actions
    handleOptionSelect,
    nextTurn,
    resetGame,
    addPlayer,
    updatePlayerName,
    toggleGender,
    removePlayer,
    startGame,
    skipQuestion,
    processContent
  };
}
